import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Switch, Space, App, Tooltip, Alert, Radio } from 'antd';
import { LockOutlined, UnlockOutlined, PlusOutlined, UserAddOutlined, ExclamationCircleOutlined, EditOutlined, TeamOutlined, DeleteOutlined, KeyOutlined, StopOutlined, CopyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createAuthApi, PasswordStrengthIndicator, usePasswordStrength, RoleAssignmentDialog } from '@zero/auth-admin-ui';
import { usePermissions } from '@zero/auth-client';
import { useGridPageSize, GRID_PAGE_SIZE_OPTIONS } from '../../hooks/useGridPageSize.js';
import { RowActions } from '@fms/shared';

const ROLES_MODAL_Z      = 1000;
const ROLES_ASSIGN_DLG_Z = 1100;   // > outer → inner luôn ở trên

const MIN_PW = 12;

export function UsersPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const { has } = usePermissions();
  const canReset = has?.('Auth.User.ResetPassword');
  const [resetResult, setResetResult] = useState(null);   // null | { username, tempPassword }
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');   // 'all' | 'active' | 'locked' — mặc định chỉ hiện active
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useGridPageSize('users', 25);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm] = Form.useForm();
  const newPw = Form.useWatch('InitialPassword', createForm) ?? '';
  const { valid: pwValid } = usePasswordStrength(newPw, MIN_PW);
  const [editing, setEditing]     = useState(null);   // null | user object
  const [editBusy, setEditBusy]   = useState(false);
  const [editForm]  = Form.useForm();

  // Roles management dialog (outer) + RoleAssignmentDialog (inner)
  const [rolesUser, setRolesUser]           = useState(null);     // null | user object
  const [userRoles, setUserRoles]           = useState([]);
  const [userRolesLoading, setUserRolesLoading] = useState(false);
  const [rolesCatalog, setRolesCatalog]     = useState([]);
  const [shipUnits, setShipUnits]           = useState([]);   // [{ ID: ShipGuid, Name }] cho scope Vessel
  const [assignOpen, setAssignOpen]         = useState(false);

  const submitCreate = async (values) => {
    setCreateBusy(true);
    try {
      await api.createUser({
        Username:        values.Username,
        Email:           values.Email || null,
        FullName:        values.FullName || null,
        InitialPassword: values.InitialPassword,
      });
      message.success(t('user.createdAt') + ' ' + new Date().toLocaleTimeString());
      setCreateOpen(false);
      createForm.resetFields();
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setCreateBusy(false);
    }
  };

  const refresh = () => {
    setLoading(true);
    api.listUsers({ offset: 0, limit: 100 })
      .then((r) => setUsers(r.data?.Data?.Items ?? []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  // Lọc client-side theo trạng thái (active/locked) + tìm theo username/họ tên/email.
  const filteredUsers = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter === 'active' && !u.IsActive) return false;
      if (statusFilter === 'locked' && u.IsActive)  return false;
      if (!q) return true;
      return [u.Username, u.FullName, u.Email]
        .some((f) => (f ?? '').toLowerCase().includes(q));
    });
  }, [users, statusFilter, searchText]);

  // Catalog role cho dialog gán vai trò: label hiện CẢ mã (Name, vd "NRPT.Captain")
  // lẫn tên mô tả (Description, vd "Create & submit report"). Dialog thư viện render
  // `label: r.Name` → ghép sẵn vào field Name (chỉ dùng cho dialog, không đụng data gốc).
  const rolesForAssign = useMemo(() => rolesCatalog.map((r) => ({
    ...r,
    Name: r.Description ? `${r.Name} — ${r.Description}` : r.Name,
  })), [rolesCatalog]);

  // Tra mô tả (tên) vai trò theo RoleID — bảng "Vai trò của user" chỉ có mã (RoleName),
  // ghép thêm Description từ catalog để hiện cả mã lẫn tên.
  const roleDescById = useMemo(
    () => Object.fromEntries(rolesCatalog.map((r) => [r.RoleID, r.Description])),
    [rolesCatalog],
  );

  // Tra tên tàu theo ShipGuid — để "Chi tiết phạm vi" hiện tên tàu thay vì GUID.
  const shipNameByGuid = useMemo(
    () => Object.fromEntries(shipUnits.map((s) => [String(s.ID).toLowerCase(), s.Name])),
    [shipUnits],
  );

  const openEdit = (u) => {
    editForm.setFieldsValue({
      FullName: u.FullName ?? '',
      Email:    u.Email    ?? '',
    });
    setEditing(u);
  };
  const openRolesDialog = async (u) => {
    setRolesUser(u);
    setUserRolesLoading(true);
    try {
      const [rolesRes, catalogRes, shipsRes] = await Promise.all([
        api.listUserRoles(u.UserID),
        rolesCatalog.length === 0 ? api.listRoles({ offset: 0, limit: 200 }) : Promise.resolve(null),
        // Danh sách tàu (ShipGuid) cho scope Vessel — bỏ qua nếu không có quyền xem tàu.
        shipUnits.length === 0
          ? axios.get('/api/master-data/ships').catch(() => null)
          : Promise.resolve(null),
      ]);
      setUserRoles(rolesRes.data?.Data ?? []);
      if (catalogRes) setRolesCatalog(catalogRes.data?.Data?.Items ?? []);
      if (shipsRes) {
        const ships = shipsRes.data?.Data ?? [];
        setShipUnits(ships.map((s) => ({
          ID:   s.ShipID,   // = ShipGuid (repo alias ShipGuid AS ShipID)
          Name: s.ShipCode ? `${s.ShipName} (${s.ShipCode})` : s.ShipName,
        })));
      }
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setUserRolesLoading(false);
    }
  };

  const refreshUserRoles = async () => {
    if (!rolesUser) return;
    setUserRolesLoading(true);
    try {
      const r = await api.listUserRoles(rolesUser.UserID);
      setUserRoles(r.data?.Data ?? []);
    } finally {
      setUserRolesLoading(false);
    }
  };

  const submitAssign = async (payload) => {
    try {
      await api.assignUserRole(rolesUser.UserID, {
        RoleID:       payload.roleID,
        ScopeType:    payload.scopeType,
        // ShipGuid là CHUỖI (GUID) → phải gửi qua ScopeItems (BE ưu tiên), KHÔNG phải
        // ScopeValues (int, GUID sẽ rớt ⇒ scope lưu rỗng ⇒ "Chi tiết phạm vi" trống).
        ScopeItems:   (payload.scopeIDs ?? []).map(String),
        Reason:       payload.reason ?? null,
        ExpiresAtUTC: payload.expiresAtUTC ?? null,
      });
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      refreshUserRoles();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    }
  };

  const removeRole = (ur) => modal.confirm({
    title:   t('user.roles.removeConfirmTitle'),
    content: t('user.roles.removeConfirmContent', { role: ur.RoleName }),
    icon:    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
    okText:  t('common.delete'),
    okButtonProps: { danger: true },
    cancelText: t('common.cancel'),
    zIndex:  ROLES_ASSIGN_DLG_Z + 50,   // confirm trên cả outer Modal
    onOk:    async () => {
      try {
        await api.unassignUserRole(rolesUser.UserID, ur.UserRoleID);
        refreshUserRoles();
      } catch (err) {
        message.error(err?.response?.data?.Message ?? err.message);
      }
    },
  });

  const submitEdit = async (values) => {
    setEditBusy(true);
    try {
      await api.updateUser(editing.UserID, {
        Email:    values.Email    || null,
        FullName: values.FullName || null,
      });
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      setEditing(null);
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setEditBusy(false);
    }
  };

  // Reset pass: sinh mật khẩu tạm + ép đổi lần đầu; hiện mật khẩu tạm cho admin copy 1 lần.
  const doResetPass = (u) => modal.confirm({
    title:   t('pwAdmin.reset.confirmTitle'),
    icon:    <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
    content: t('pwAdmin.reset.confirmContent', { username: u.Username }),
    okText:     t('pwAdmin.reset.ok'),
    cancelText: t('common.cancel'),
    onOk: async () => {
      try {
        const r = await axios.post(`/api/account-admin/users/${u.UserID}/reset-pass`);
        setResetResult({ username: u.Username, tempPassword: r.data?.Data?.TempPassword ?? '' });
        refresh();
      } catch (err) {
        message.error(err?.response?.data?.Message ?? err.message);
        throw err;   // giữ modal mở khi lỗi
      }
    },
  });

  // Lock pass: đặt mật khẩu random dài (không hiện) ⇒ user chỉ đăng nhập được bằng Google.
  const doLockPass = (u) => modal.confirm({
    title:   t('pwAdmin.lock.confirmTitle'),
    icon:    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
    content: t('pwAdmin.lock.confirmContent', { username: u.Username, email: u.Email || '—' }),
    okText:     t('pwAdmin.lock.ok'),
    okButtonProps: { danger: true },
    cancelText: t('common.cancel'),
    onOk: async () => {
      try {
        await axios.post(`/api/account-admin/users/${u.UserID}/lock-pass`);
        message.success(t('pwAdmin.lock.done'));
        refresh();
      } catch (err) {
        message.error(err?.response?.data?.Message ?? err.message);
        throw err;
      }
    },
  });

  const columns = [
    { title: t('common.id'),       dataIndex: 'UserID',    width: 80 },
    { title: t('user.username'),   dataIndex: 'Username',  render: (v) => <strong>{v}</strong> },
    { title: t('user.fullName'),   dataIndex: 'FullName',  render: (v) => v ?? <span style={{ opacity: 0.4 }}>–</span> },
    { title: t('user.email'),      dataIndex: 'Email' },
    { title: t('user.active'),     dataIndex: 'IsActive',  width: 100, render: (v) => v
        ? <Tag color="success">{t('common.active')}</Tag>
        : <Tag color="error">{t('common.locked')}</Tag> },
    { title: '2FA',                dataIndex: 'TwoFactorEnabled', width: 80, render: (v) => v ? <Tag>{t('common.yes')}</Tag> : '-' },
    { title: t('user.oauth', 'Google'), dataIndex: 'OAuthProvider', width: 100,
      render: (v) => v ? <Tag color="blue">{t('user.oauthLinked', 'Đã liên kết')}</Tag> : '-' },
    // Cột thao tác: gộp mọi thao tác vào 1 nút mở menu — quy ước chung mọi grid FMS.
    { title: t('common.actions'), key: 'a', width: 90, align: 'center', render: (_, u) => (
        <RowActions items={[
          { key: 'edit', label: t('common.edit'), onClick: () => openEdit(u) },
          { key: 'roles', icon: <TeamOutlined />, label: t('user.roles.manage'), onClick: () => openRolesDialog(u) },
          canReset && { key: 'reset', icon: <KeyOutlined />, label: t('pwAdmin.reset.tip'), onClick: () => doResetPass(u) },
          // Gỡ liên kết Google: bình thường KHÔNG cần (đăng nhập Google dò theo email, liên kết
          // cũ nằm ở tài khoản khác thì hệ thống tự chuyển). Dùng khi muốn CẮT đường đăng nhập
          // Google của một tài khoản.
          u.OAuthProvider && { key: 'unlink', label: t('user.unlinkOAuth', 'Gỡ liên kết Google'),
            onClick: () => modal.confirm({
              title:   t('user.unlinkOAuthConfirmTitle', 'Gỡ liên kết Google?'),
              content: t('user.unlinkOAuthConfirmContent',
                { username: u.Username, defaultValue: 'Tài khoản {{username}} sẽ không đăng nhập bằng Google được nữa (mật khẩu vẫn dùng bình thường). Lần sau đăng nhập Google bằng đúng email của tài khoản này thì hệ thống tự liên kết lại.' }),
              icon:    <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
              okText:  t('user.unlinkOAuth', 'Gỡ liên kết Google'),
              cancelText: t('common.cancel'),
              onOk:    () => api.unlinkUserOAuth(u.UserID).then(refresh),
            }) },
          canReset && { key: 'lockpw', icon: <StopOutlined />, danger: true, label: t('pwAdmin.lock.tip'), onClick: () => doLockPass(u) },
          u.IsActive
            ? { key: 'lock', icon: <LockOutlined />, danger: true, label: t('common.lock'),
                onClick: () => modal.confirm({
                  title:       t('user.lockConfirmTitle'),
                  content:     t('user.lockConfirmContent', { username: u.Username }),
                  icon:        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
                  okText:      t('common.lock'),
                  okButtonProps: { danger: true },
                  cancelText:  t('common.cancel'),
                  onOk:        () => api.lockUser(u.UserID, 'Admin lock').then(refresh),
                }) }
            : { key: 'unlock', icon: <UnlockOutlined />, label: t('common.unlock'),
                onClick: () => api.unlockUser(u.UserID).then(refresh) },
        ]} />
      ),
    },
  ];

  return (
    <Card size="small" extra={
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
        {t('common.createNew')}
      </Button>
    }>
      <Space style={{ marginBottom: 12, flexWrap: 'wrap', rowGap: 8 }} size="middle">
        <Radio.Group
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          optionType="button"
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="all">{t('usersPage.filter.all')}</Radio.Button>
          <Radio.Button value="active">{t('common.active')}</Radio.Button>
          <Radio.Button value="locked">{t('common.locked')}</Radio.Button>
        </Radio.Group>
        <Input.Search
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t('usersPage.filter.searchPlaceholder')}
          style={{ width: 320 }}
          autoComplete="off"
        />
      </Space>

      <Table dataSource={filteredUsers} columns={columns} loading={loading} rowKey="UserID" size="small" bordered
             pagination={{
               pageSize,
               showSizeChanger: true,
               pageSizeOptions: GRID_PAGE_SIZE_OPTIONS,
               onShowSizeChange: (_, size) => setPageSize(size),
             }}
             scroll={{ y: 'calc(100vh - 240px)' }} />

      <Modal maskClosable={false}
        open={createOpen}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        title={<span><UserAddOutlined /> {t('user.create')}</span>}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" onFinish={submitCreate} requiredMark={false} autoComplete="off">
          <Form.Item label={t('user.username')} name="Username"
                     rules={[{ required: true, min: 3, max: 100 }]}>
            <Input autoFocus autoComplete="off" />
          </Form.Item>
          <Form.Item label={t('user.fullName')} name="FullName"
                     rules={[{ max: 200 }]}>
            <Input autoComplete="off" placeholder={t('user.fullNamePlaceholder')} />
          </Form.Item>
          <Form.Item label={t('user.email')} name="Email"
                     rules={[{ type: 'email' }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item label={t('user.initialPassword')} name="InitialPassword"
                     rules={[{ required: true, min: MIN_PW }]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <PasswordStrengthIndicator password={newPw} minLength={MIN_PW} />
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Button onClick={() => { setCreateOpen(false); createForm.resetFields(); }} style={{ marginRight: 8 }}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={createBusy} disabled={!pwValid}>
              {t('common.create')}
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal maskClosable={false}
        open={editing !== null}
        onCancel={() => setEditing(null)}
        title={<span><EditOutlined /> {t('user.edit')}</span>}
        footer={null}
        width={480}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" onFinish={submitEdit} requiredMark={false} autoComplete="off">
          <Form.Item label={t('user.username')}>
            <Input value={editing?.Username ?? ''} disabled />
          </Form.Item>
          <Form.Item label={t('user.fullName')} name="FullName" rules={[{ max: 200 }]}>
            <Input autoComplete="off" placeholder={t('user.fullNamePlaceholder')} />
          </Form.Item>
          <Form.Item label={t('user.email')} name="Email" rules={[{ type: 'email' }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Button onClick={() => setEditing(null)} style={{ marginRight: 8 }}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={editBusy}>
              {t('common.save')}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Outer: list user's role assignments + button "Assign new"
          Inner RoleAssignmentDialog dùng zIndex CAO HƠN tuyệt đối (1100 > 1000)
          để tránh bug AntD nested-Modal counter — lần 2+ inner KHÔNG bị tụt
          xuống dưới outer dù counter của AntD đã bump. */}
      <Modal maskClosable={false}
        open={rolesUser !== null}
        onCancel={() => { setRolesUser(null); setUserRoles([]); }}
        title={<span><TeamOutlined /> {t('user.roles.title', { username: rolesUser?.Username ?? '' })}</span>}
        footer={null}
        width={780}
        zIndex={ROLES_MODAL_Z}
        destroyOnHidden
      >
        <Space style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignOpen(true)}>
            {t('user.roles.assignNew')}
          </Button>
        </Space>
        <Table
          dataSource={userRoles}
          rowKey="UserRoleID"
          size="small"
          bordered
          loading={userRolesLoading}
          pagination={false}
          locale={{ emptyText: t('user.roles.none') }}
          columns={[
            { title: t('common.id'),    dataIndex: 'UserRoleID', width: 70 },
            { title: t('role.label'),   dataIndex: 'RoleName',
              render: (v, r) => (
                <span>
                  <strong>{v}</strong>
                  {roleDescById[r.RoleID] && (
                    <span style={{ color: '#888', marginLeft: 6 }}>— {roleDescById[r.RoleID]}</span>
                  )}
                </span>
              ) },
            { title: t('scope.type'),   dataIndex: 'ScopeType',  width: 140,
              render: (v) => <Tag color={v === 'Global' ? 'red' : v === 'Tenant' ? 'blue' : 'cyan'}>{v === 'Tenant' ? 'Company' : v}</Tag> },
            { title: t('permission.scopeDetails'), key: 'sv',
              render: (_, r) => {
                // Ship scope = GUID nằm trong ScopeItems (string); ScopeValues chỉ chứa scope dạng số.
                const items = (r.ScopeItems?.length ? r.ScopeItems : (r.ScopeValues ?? [])).map(String);
                return items.length
                  ? <span>{items.map((id) => (
                      <Tag key={id} color="geekblue" style={{ marginBottom: 2 }}>
                        {shipNameByGuid[id.toLowerCase()] ?? id}
                      </Tag>
                    ))}</span>
                  : <span style={{ opacity: 0.4 }}>–</span>;
              } },
            { title: t('role.expires'), dataIndex: 'ExpiresAtUTC', width: 160,
              render: (v) => v ? new Date(v).toLocaleString() : <span style={{ opacity: 0.4 }}>–</span> },
            { title: t('common.actions'), key: 'a', width: 90,
              render: (_, r) => (
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeRole(r)} />
              ) },
          ]}
        />
      </Modal>

      <RoleAssignmentDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        roles={rolesForAssign}
        units={{ Vessel: shipUnits }}
        onConfirm={submitAssign}
        zIndex={ROLES_ASSIGN_DLG_Z}
      />

      {/* Mật khẩu tạm sau Reset pass — hiện ĐÚNG 1 LẦN cho admin copy, đóng là mất. */}
      <Modal maskClosable={false}
        open={resetResult !== null}
        onCancel={() => setResetResult(null)}
        title={<span><KeyOutlined /> {t('pwAdmin.reset.resultTitle')}</span>}
        okText={t('pwAdmin.close')}
        onOk={() => setResetResult(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
        width={460}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('pwAdmin.reset.resultWarn', { username: resetResult?.username ?? '' })}
        />
        <Space.Compact style={{ width: '100%' }}>
          <Input readOnly value={resetResult?.tempPassword ?? ''}
                 style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 600 }} />
          <Button type="primary" icon={<CopyOutlined />} onClick={() => {
            navigator.clipboard?.writeText(resetResult?.tempPassword ?? '');
            message.success(t('pwAdmin.copied'));
          }}>{t('pwAdmin.copy')}</Button>
        </Space.Compact>
      </Modal>
    </Card>
  );
}
